import React from 'react';

import User from '../../../Api/Models/User';
import * as formatters from '../../../utils/formatters';
import DeleteButton from '../../elements/DeleteButton';
import EditButton from '../../elements/EditButton';
import ProtectedLink from '../../elements/ProtectedLink';
import ViewButton from '../../elements/ViewButton';
import BooleanIcon from '../../elements/booleanIcon';
import SortButton, { SortProps } from '../../elements/common/SortButton';
import { TableCell as Cell } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import { PERMISSION } from '../../helpers/Permissions';
import { UpdateItemsFunction } from '../../hooks/useAutoPaginator';

import TableBody from 'crm/elements/common/TableBody';
import useTableSettings from 'crm/hooks/useTableSettings';

type Props = Readonly<{
    users: User[];
    update: UpdateItemsFunction;
    sortProps: SortProps;
}>;

export const USER_TABLE_DEFAULT_ID = 'UserTableDefault';

function UserTableDefault({ users, update, sortProps }: Props) {
    const settings = useTableSettings(USER_TABLE_DEFAULT_ID);

    return (
        <table className="table model-list">
            <TableHead settings={settings}>
                <th>Action</th>
                <SortButton column="id" {...sortProps} />
                {User.isKycExposed() && <SortButton column="kycs.number" label="CPF" {...sortProps} />}
                <SortButton column="token" {...sortProps} />
                <SortButton column="name" {...sortProps} />
                <SortButton column="last_name" {...sortProps} />
                <SortButton column="email" label="E-mail" {...sortProps} />
                <SortButton column="phone_confirmed" {...sortProps} />
                <SortButton column="is_kyc_confirmed" label="KYC confirmed" {...sortProps} />
                <SortButton column="created_at" {...sortProps} />
                <SortButton column="reseller_debit" {...sortProps} />
                <SortButton column="phone" {...sortProps} />
                <SortButton column="country" {...sortProps} />
                <SortButton column="state" {...sortProps} />
                <SortButton column="user_accounts.balance_default" label="Balance" {...sortProps} />
                <SortButton column="deposit_sum" {...sortProps} />
                <SortButton column="purchase_sum" {...sortProps} />
                <SortButton column="withdrawal_sum" {...sortProps} />
                <SortButton column="affiliate_id" label="Affiliate" {...sortProps} />
            </TableHead>
            <UserTableBody users={users} update={update} />
        </table>
    );
}

function UserTableBody({ users, update }: Readonly<{ users: User[]; update: UpdateItemsFunction }>) {
    const { formatDateStringWithTimezone } = formatters;
    const settings = useTableSettings(USER_TABLE_DEFAULT_ID);

    return (
        <TableBody settings={settings}>
            {users.map((user) => (
                <tr key={user.id} className={user.isDeleted() ? 'deleted-row' : ''}>
                    <td>
                        <ViewButton url={`/users/${user.id}`} permissions={[PERMISSION.VIEW_USER]} />
                        {user.isDeleted() ? null : (
                            <>
                                <EditButton url={`/users/${user.id}/edit`} permissions={[PERMISSION.UPDATE_USER]} />
                                <DeleteButton
                                    url={`/crea/api/user/${user.id}`}
                                    action={update}
                                    permissions={[PERMISSION.DELETE_USER]}
                                />
                            </>
                        )}
                    </td>
                    <Cell>{user.id}</Cell>
                    {User.isKycExposed() && <Cell>{user.cpf}</Cell>}
                    <Cell>{user.token}</Cell>
                    <Cell>{user.name}</Cell>
                    <Cell>{user.last_name}</Cell>
                    <Cell>{user.email}</Cell>
                    <td className="text-center">
                        <BooleanIcon value={user.phone_confirmed} />
                    </td>
                    <td className="text-center">
                        <BooleanIcon value={user.is_kyc_confirmed} />
                    </td>
                    <Cell>{formatDateStringWithTimezone(user.created_at)}</Cell>
                    <Cell>{user.reseller_debit_formatted}</Cell>
                    <Cell>{user.phone}</Cell>
                    <Cell>{user.country}</Cell>
                    <Cell>{user.state}</Cell>
                    <Cell>{user.balance_formatted}</Cell>
                    <Cell>{user.deposit_sum}</Cell>
                    <Cell>{user.purchase_sum}</Cell>
                    <Cell>{user.withdrawal_sum}</Cell>
                    <Cell showIf={user.affiliate_id > 0}>
                        <ProtectedLink
                            url={`/affiliates/${user.affiliate_id}`}
                            permissions={[PERMISSION.VIEW_AFFILIATE]}
                            value={user.affiliate_id}
                        />
                    </Cell>
                </tr>
            ))}
        </TableBody>
    );
}

export default UserTableDefault;
